import React from 'react';

import CheckboxFormField from '@components/form-fields/CheckboxFormField';
import { TableCell, TableRow } from '@mui/material';
import TableHead from '@mui/material/TableHead';
import TableSortLabel from '@mui/material/TableSortLabel';
import { MAIN_THEME } from '@styles';
import { TableSortOrder } from '@utils/table-utils';
import { isEmpty } from '@utils/validationUtils';

import {
  EnhancedTableHeader,
  EnhancedTableHeaderType
} from './widgets/shared-with-old';

import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';

export interface HeadRow {
  disablePadding: boolean;
  id: string;
  label: string;
  numeric: boolean;
}

interface IListingTableHeadProps {
  headRows: Record<string, EnhancedTableHeader>;
  numSelected: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  onSelectAllClick: (name: string, checked: boolean) => void;
  order?: TableSortOrder;
  orderBy?: string | undefined;
  rowCount: number;
  disableSelection?: boolean;
  forceDisableSelectAll?: boolean;
  disabledSorting?: boolean;
  forceShowSelectColumn?: boolean;
}

const useStyles = makeStyles()(() => ({
  cellRoot: {
    color: `${MAIN_THEME.color.black} !important`,
    fontSize: `${MAIN_THEME.fontSize.sm} !important`,
    lineHeight: '13px !important',
    height: '34px !important',
    fontFamily: `${MAIN_THEME.font.bold} !important`,
    border: 'none !important',
    padding: '4px 6px !important'
  },
  checkboxCellRoot: {
    width: '14px !important',
    padding: '0 14px 0 8px !important',
    border: 'none !important',
    height: '34px !important',
    fontFamily: MAIN_THEME.font.regular
  },
  paddedCellRoot: {
    padding: '4px 2px 4px 8px !important'
  }
}));

const ListingTableHead: React.FC<IListingTableHeadProps> = ({
  headRows,
  onSelectAllClick,
  order,
  orderBy,
  numSelected,
  rowCount,
  onRequestSort,
  disableSelection,
  forceDisableSelectAll = false,
  disabledSorting = false,
  forceShowSelectColumn = false
}) => {
  const { classes } = useStyles();

  const createSortHandler =
    (property: string) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        <TableCell
          role="checkbox"
          padding="none"
          classes={{ root: classes.checkboxCellRoot }}
        >
          {!disableSelection && (
            <CheckboxFormField
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={numSelected === rowCount && rowCount !== 0}
              disabled={rowCount === 0 || forceDisableSelectAll}
              onChange={onSelectAllClick}
              name={'Select All'}
              inputOnly
            />
          )}
        </TableCell>

        {Object.keys(headRows)
          .filter((key) => !headRows[key]?.hidden)
          .map((headerKey, headerIndex) => {
            const col = headRows[headerKey];

            // Sort is enabled by default; disabled only if:
            // 1) global disabledSorting is true, or
            // 2) column.disableSort is true, or
            // 3) column type is Input (never sortable)
            const sortable =
              !disabledSorting &&
              !(col?.disableSort ?? false) &&
              col?.type !== EnhancedTableHeaderType.Input;

            const isActive =
              sortable && orderBy === (col?.filterField || headerKey);

            return (
              <TableCell
                key={headerKey}
                padding="none"
                sortDirection={isActive ? order : (false as any)}
                classes={{
                  root: clsx(classes.cellRoot, {
                    [classes.paddedCellRoot]:
                      (!disableSelection || forceShowSelectColumn) &&
                      !headerIndex
                  })
                }}
              >
                {!isEmpty(col.title) && (
                  <TableSortLabel
                    active={isActive}
                    direction={isActive ? (order as any) : 'asc'}
                    onClick={
                      sortable
                        ? createSortHandler(col?.filterField || headerKey)
                        : undefined
                    }
                    disabled={!sortable}
                    hideSortIcon={!isActive} // only show arrow on active sort column
                  >
                    {col.title}
                  </TableSortLabel>
                )}
              </TableCell>
            );
          })}
      </TableRow>
    </TableHead>
  );
};

export default ListingTableHead;
